import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!turnstileSiteKey) {
      return undefined;
    }

    const existingScript = document.querySelector('script[data-turnstile-script="true"]');

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current || turnstileWidgetIdRef.current !== null) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => {
          setTurnstileToken(token);
          setError('');
        },
        'expired-callback': () => {
          setTurnstileToken('');
        },
        'error-callback': () => {
          setTurnstileToken('');
          setError('Turnstile verification failed to load. Please try again.');
        }
      });
    };

    const onScriptLoad = () => {
      renderWidget();
    };

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.dataset.turnstileScript = 'true';
      script.onload = onScriptLoad;
      document.head.appendChild(script);
    } else if (window.turnstile) {
      onScriptLoad();
    } else {
      existingScript.addEventListener('load', onScriptLoad);
    }

    return () => {
      if (existingScript) {
        existingScript.removeEventListener('load', onScriptLoad);
      }
    };
  }, [turnstileSiteKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (turnstileSiteKey && !turnstileToken) {
      setError('Please complete the Turnstile verification.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password, turnstileToken: turnstileSiteKey ? turnstileToken : undefined });
      login(response.data.token, response.data.store);
      if (response.data.store?.role === 'app_admin' || response.data.store?.role === 'business_admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Welcome Back</h1>
        <p className="auth-subtitle">Sign in to your business or site account</p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {turnstileSiteKey && <div className="form-group"><div ref={turnstileContainerRef} /></div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Registration temporarily disabled */}
        {/* <p className="text-center mt-3" style={{ color: 'var(--text-light)' }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: 'var(--pink-dark)', fontWeight: 600 }}>
            Register here
          </Link>
        </p> */}
      </div>
    </div>
  );
};

export default Login;
